import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { Typography, Button, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Card from 'components/Card/Card.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardBody from 'components/Card/CardBody.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import BubbleContainer from 'components/BubbleContainer/BubbleContainer';
import { D2RSService } from '../../services/d2rsService';
import styles from 'assets/jss/material-kit-react/views/profilePage.js';
import config from '../../config/config.json';

// Import class icons
import amazonIcon from 'assets/img/amazonicon.png';
import assassinIcon from 'assets/img/assassinicon.png';
import barbarianIcon from 'assets/img/barbarianicon.png';
import druidIcon from 'assets/img/druidicon.png';
import necromancerIcon from 'assets/img/necromancericon.png';
import paladinIcon from 'assets/img/paladinicon.png';
import sorceressIcon from 'assets/img/sorceressicon.png';

const classIcons = {
  amazon: amazonIcon,
  assassin: assassinIcon,
  barbarian: barbarianIcon,
  druid: druidIcon,
  necromancer: necromancerIcon,
  paladin: paladinIcon,
  sorceress: sorceressIcon,
} as const;

const useStyles = makeStyles<Theme>((theme) => ({
  mainContainer: {
    position: 'relative',
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '6px',
    color: theme.palette.text.primary,
  },
  container: {
    ...styles.container,
    position: 'relative',
    zIndex: 3,
  },
  contentContainer: {
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
    zIndex: 1100,
    '& .MuiTabs-root': {
      zIndex: 1200,
    },
    '& .MuiTab-root': {
      zIndex: 1200,
    },
  },
  packImage: {
    width: '100%',
    maxHeight: '400px',
    objectFit: 'cover',
    borderRadius: '8px',
    marginBottom: theme.spacing(3),
  },
  price: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    fontSize: '2rem',
    marginBottom: theme.spacing(3),
  },
  description: {
    marginBottom: theme.spacing(3),
  },
  tag: {
    margin: theme.spacing(0.5),
  },
  tagsContainer: {
    marginBottom: theme.spacing(3),
  },
  entryContainer: {
    marginTop: theme.spacing(2),
  },
  buyButton: {
    marginTop: theme.spacing(3),
  },
  viewMoreButton: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  viewMoreLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  metadata: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2),
  },
  pickitLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  card: {
    marginTop: '30px',
    marginBottom: '30px',
    width: '100%',
  },
  cardHeader: {
    width: 'auto',
    textAlign: 'center',
    marginLeft: '20px',
    marginRight: '20px',
    marginTop: '-40px',
    padding: '20px 0',
    marginBottom: '15px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
  },
  classIcon: {
    width: '32px',
    height: '32px',
    objectFit: 'contain',
  },
}));

interface PickitPackEntry {
  pack_id: number;
  pickit_id: number;
  amount: number;
  character_class?: string;
}

interface DynamicPickitPack {
  id: number;
  title: string;
  description: string;
  price: number;
  image_path: string;
  created_at: string;
  updated_at: string;
  entries: PickitPackEntry[];
  tags: string[];
  character_class?: string;
}

export default function DynamicPickitPackDetailPage() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const {
    data: pack,
    isLoading,
    error,
  } = useQuery<DynamicPickitPack, Error>({
    queryKey: ['dynamic-pickit-pack', id],
    queryFn: async () => {
      if (!id) throw new Error('No pack ID provided');
      return D2RSService.fetchDynamicPickitPackById(parseInt(id, 10));
    },
    enabled: !!id,
  });

  const getCharacterClass = (tags: string[]) => {
    const classNames = Object.keys(classIcons);
    return tags.find((tag) => classNames.includes(tag.toLowerCase()))?.toLowerCase();
  };

  useDocumentTitle(pack ? `${pack.title} - ${t('common.pickit.pack')}` : t('common.loading.title'));

  if (isLoading) {
    return (
      <div>
        <div className={classes.mainContainer}>
          <div className={classes.container}>
            <div className={classes.contentContainer}>
              <Typography>{t('common.loading.fetchingData')}</Typography>
            </div>
          </div>
        </div>
        <BubbleContainer pageKey="pickit-pack" helpText={t('help.pickitPack')} />
      </div>
    );
  }

  if (error) {
    console.error('Error fetching pack:', error);
    return (
      <div>
        <div className={classes.mainContainer}>
          <div className={classes.container}>
            <div className={classes.contentContainer}>
              <Typography color="error">
                {t('common.errors.fetchFailed')}: {error.message}
              </Typography>
            </div>
          </div>
        </div>
        <BubbleContainer pageKey="pickit-pack" helpText={t('help.pickitPack')} />
      </div>
    );
  }

  if (!pack) {
    return (
      <div>
        <div className={classes.mainContainer}>
          <div className={classes.container}>
            <div className={classes.contentContainer}>
              <Typography>{t('common.errors.notFound')}</Typography>
            </div>
          </div>
        </div>
        <BubbleContainer pageKey="pickit-pack" helpText={t('help.pickitPack')} />
      </div>
    );
  }

  return (
    <div>
      <div className={classes.mainContainer}>
        <div className={classes.container}>
          <div className={classes.contentContainer}>
            <Card className={classes.card}>
              <CardHeader color="primary" className={classes.cardHeader}>
                {pack.tags && getCharacterClass(pack.tags) && (
                  <Link to={`/class/${getCharacterClass(pack.tags)}?tab=packs`}>
                    <img
                      src={classIcons[getCharacterClass(pack.tags) as keyof typeof classIcons]}
                      alt={getCharacterClass(pack.tags)}
                      className={classes.classIcon}
                    />
                  </Link>
                )}
                <Typography variant="h4">{pack.title}</Typography>
              </CardHeader>
              <CardBody>
                <GridContainer>
                  <GridItem xs={12}>
                    {pack.image_path && (
                      <img
                        src={`${config.backend_base_url}image/${pack.image_path}`}
                        alt={pack.title}
                        className={classes.packImage}
                      />
                    )}

                    <Typography variant="h4" className={classes.price}>
                      ${pack.price.toFixed(2)}
                    </Typography>

                    <Typography variant="body1" className={classes.description}>
                      {pack.description}
                    </Typography>

                    <div className={classes.tagsContainer}>
                      {pack.tags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          className={classes.tag}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </div>

                    <Typography variant="h6">{t('pickit.pack.contents')}</Typography>
                    <div className={classes.entryContainer}>
                      {pack.entries.map((entry) => (
                        <Typography key={entry.pickit_id}>
                          • {entry.amount}x Pickit #
                          <Link to={`/product/${entry.pickit_id}`} className={classes.pickitLink}>
                            {entry.pickit_id}
                          </Link>
                        </Typography>
                      ))}
                    </div>

                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      className={classes.buyButton}
                    >
                      {t('pickit.pack.buy')}
                    </Button>

                    {pack.tags && getCharacterClass(pack.tags) && (
                      <div className={classes.viewMoreButton}>
                        <Link
                          to={`/class/${getCharacterClass(pack.tags)}?tab=packs`}
                          className={classes.viewMoreLink}
                        >
                          <img
                            src={
                              classIcons[getCharacterClass(pack.tags) as keyof typeof classIcons]
                            }
                            alt={getCharacterClass(pack.tags)}
                            className={classes.classIcon}
                          />
                          {t('pickit.pack.viewMoreForClass', {
                            className: getCharacterClass(pack.tags),
                          })}
                        </Link>
                      </div>
                    )}

                    <Typography variant="body2" className={classes.metadata}>
                      {t('common.lastUpdated')}: {new Date(pack.updated_at).toLocaleDateString()}
                    </Typography>
                  </GridItem>
                </GridContainer>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
      <BubbleContainer pageKey="pickit-pack" helpText={t('help.pickitPack')} />
    </div>
  );
}
